
/*
 * The artwork on the dashboard hero.
 *
 * Deliberately not themed, and kept in its own file so that stays true.
 *
 * Every other colour in the app is a theme token, because every other colour
 * sits on the page and has to follow it. These do not: they are painted on
 * the hero card, which is a dark green gradient in all three themes, at 0.15
 * to 0.28 opacity. They are illustration, and an illustration that repaints
 * itself when somebody picks a different theme is not the same illustration.
 *
 * So the literals here are the point rather than an oversight, and the file
 * imports nothing -- not the theme, and not even the list of variants, which
 * lives in utils/heroArtwork.js. This file takes a `variant` and draws it;
 * who chose it and where that choice is kept is somebody else's business.
 * The test asserts both of those properties, because a rule kept only by a
 * comment is a rule the next codemod run quietly undoes.
 */

function MushroomAmanita({ color = '#c0392b', size = 80, opacity = 1, flat = false, style = {} }) {
  return (
    <svg width={size} height={size * 1.3} viewBox="0 0 80 104" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity, ...style }}>
      <rect x="30" y="54" width="20" height="40" rx="5" fill={flat ? color : '#d4b896'} />
      {!flat && <rect x="26" y="66" width="28" height="5" rx="2" fill="#c9a87c" opacity="0.6" />}
      <ellipse cx="40" cy="50" rx="36" ry="22" fill={color} />
      {!flat && <ellipse cx="40" cy="44" rx="36" ry="14" fill={color} opacity="0.6" />}
      {!flat && <circle cx="26" cy="42" r="5.5" fill="white" opacity="0.75" />}
      {!flat && <circle cx="44" cy="35" r="4" fill="white" opacity="0.75" />}
      {!flat && <circle cx="56" cy="45" r="4.5" fill="white" opacity="0.75" />}
      {!flat && <circle cx="34" cy="52" r="3" fill="white" opacity="0.6" />}
    </svg>
  );
}

function MushroomSlender({ color = '#8e44ad', size = 70, opacity = 1, flat = false, style = {} }) {
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 70 105" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity, ...style }}>
      <rect x="30" y="45" width="10" height="55" rx="4" fill={flat ? color : '#c8a97e'} />
      <ellipse cx="35" cy="42" rx="30" ry="16" fill={color} />
      {!flat && <ellipse cx="35" cy="36" rx="22" ry="10" fill={color} opacity="0.55" />}
    </svg>
  );
}

function MushroomOyster({ color = '#2c7a4b', size = 90, opacity = 1, flat = false, style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity, ...style }}>
      <path d="M10 60 Q5 30 40 20 Q70 10 80 45 Q85 70 55 78 Q25 88 10 60Z" fill={color} />
      {!flat && <path d="M30 75 Q20 50 40 30" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity="0.3" />}
      {!flat && <path d="M48 78 Q40 52 50 32" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" opacity="0.25" />}
      {!flat && <path d="M62 72 Q58 50 65 38" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" opacity="0.2" />}
    </svg>
  );
}

function MushroomPorcini({ color = '#b45309', size = 75, opacity = 1, flat = false, style = {} }) {
  return (
    <svg width={size} height={size * 1.2} viewBox="0 0 75 90" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity, ...style }}>
      <path d="M28 55 Q24 75 20 85 Q30 88 37 88 Q44 88 55 85 Q51 75 47 55Z" fill={flat ? color : '#d4b896'} />
      {!flat && <path d="M30 60 Q37 64 45 60" stroke="#c9a87c" strokeWidth="2" opacity="0.5" />}
      <ellipse cx="37" cy="50" rx="34" ry="20" fill={color} />
      {!flat && <ellipse cx="37" cy="44" rx="34" ry="12" fill={color} opacity="0.5" />}
    </svg>
  );
}

/** A spore print: scattered discs with a faint drift line through them. */
function SporeCluster({ size = 90, opacity = 1, style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity, ...style }}>
      <path d="M4 70 Q30 40 50 46 Q72 52 88 22" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
      <circle cx="16" cy="60" r="9" fill="#ffffff" opacity="0.55" />
      <circle cx="38" cy="45" r="13" fill="#ffffff" opacity="0.4" />
      <circle cx="61" cy="49" r="7" fill="#ffffff" opacity="0.6" />
      <circle cx="76" cy="28" r="10" fill="#ffffff" opacity="0.35" />
      <circle cx="52" cy="70" r="5" fill="#ffffff" opacity="0.5" />
    </svg>
  );
}

/** Branching hyphae -- the organism as it actually spends most of its life. */
function Mycelium({ size = 120, opacity = 1, style = {} }) {
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 120 84" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity, ...style }}>
      <g stroke="#ffffff" strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.7">
        <path d="M2 42 Q30 40 46 30 Q62 20 92 18" />
        <path d="M46 30 Q60 38 78 40 Q96 42 116 34" />
        <path d="M46 30 Q52 48 46 66 Q42 78 26 82" />
        <path d="M78 40 Q84 54 100 62" />
        <path d="M22 41 Q26 28 20 14" />
      </g>
      <circle cx="46" cy="30" r="2.6" fill="#ffffff" opacity="0.8" />
      <circle cx="78" cy="40" r="2.2" fill="#ffffff" opacity="0.7" />
      <circle cx="22" cy="41" r="2" fill="#ffffff" opacity="0.7" />
    </svg>
  );
}

/*
 * The ground the living mycelium grows in: a faint band of soil along the
 * bottom of the card, darkening with depth and with no edge at the surface,
 * so it reads as ground rather than as a ruled line.
 *
 * The colony itself is NOT drawn here. It moves, so it is a canvas
 * (components/organic/HeroMycelium.jsx) that the Dashboard renders above this
 * layer when 'mycelium' is chosen -- this file imports nothing, the organic
 * library included. What is here is the part that never moves, and so the
 * part that is still there with no canvas at all.
 *
 * Its height is the colony's own soil-band formula, hS = clamp(0.3 * H, 36,
 * 84) in organic/heroDirector.js's heroLayout, written as CSS against the same
 * card, so the band's top and the colony's soil surface are one line. If that
 * formula changes, change this with it.
 */
function SoilBand({ opacity = 1, style = {} }) {
  return (
    <div
      data-decor="soil"
      style={{
        height: 'clamp(36px, 30%, 84px)',
        background: 'linear-gradient(to bottom, rgba(8,22,14,0) 0%, rgba(8,22,14,0.55) 40%, rgba(4,14,8,0.9) 100%)',
        opacity,
        pointerEvents: 'none',
        ...style,
      }}
    />
  );
}

/*
 * One entry per variant. Each returns the decorative layer's children; the
 * watermark is added around them by the component below, because it is the
 * product's mark rather than decoration and stays whatever is chosen -- most
 * visibly under 'none', which is the choice for someone who finds the
 * illustration distracting.
 */
const VARIANTS = {
  // Only the still part of the living colony; see SoilBand.
  mycelium: () => (
    <SoilBand opacity={0.28}
      style={{ position: 'absolute', bottom: 0, insetInlineStart: 0, insetInlineEnd: 0 }} />
  ),

  mushrooms: () => (
    <>
      <MushroomAmanita color="#e74c3c" size={90} opacity={0.28}
        style={{ position: 'absolute', bottom: -10, insetInlineEnd: 40 }} />
      <MushroomSlender color="#9b59b6" size={65} opacity={0.22}
        style={{ position: 'absolute', bottom: 5, insetInlineEnd: 155 }} />
      <MushroomPorcini color="#e67e22" size={70} opacity={0.2}
        style={{ position: 'absolute', bottom: -5, insetInlineEnd: 240 }} />
      <MushroomOyster color="#27ae60" size={60} opacity={0.18}
        style={{ position: 'absolute', top: 16, insetInlineEnd: 310 }} />
      <MushroomAmanita color="#f1c40f" size={45} opacity={0.15}
        style={{ position: 'absolute', bottom: 20, insetInlineEnd: 335 }} />
    </>
  ),

  silhouettes: () => (
    <>
      <MushroomAmanita color="#ffffff" flat size={90} opacity={0.17}
        style={{ position: 'absolute', bottom: -10, insetInlineEnd: 44 }} />
      <MushroomSlender color="#ffffff" flat size={62} opacity={0.14}
        style={{ position: 'absolute', bottom: 6, insetInlineEnd: 152 }} />
      <MushroomPorcini color="#ffffff" flat size={68} opacity={0.12}
        style={{ position: 'absolute', bottom: -4, insetInlineEnd: 236 }} />
      <MushroomOyster color="#ffffff" flat size={58} opacity={0.1}
        style={{ position: 'absolute', top: 18, insetInlineEnd: 308 }} />
      <MushroomSlender color="#ffffff" flat size={40} opacity={0.09}
        style={{ position: 'absolute', bottom: 22, insetInlineEnd: 336 }} />
    </>
  ),

  spores: () => (
    <>
      <SporeCluster size={104} opacity={0.22}
        style={{ position: 'absolute', bottom: -8, insetInlineEnd: 36 }} />
      <Mycelium size={150} opacity={0.18}
        style={{ position: 'absolute', bottom: 10, insetInlineEnd: 150 }} />
      <SporeCluster size={66} opacity={0.14}
        style={{ position: 'absolute', top: 12, insetInlineEnd: 300 }} />
    </>
  ),

  none: () => null,
};

/**
 * The decorative layer: the watermark, and whichever artwork is chosen.
 *
 * Absolutely positioned and clipped by the caller, which owns the rounded
 * corners -- overflow:hidden must not live on the hero itself or it clips the
 * search dropdown below.
 *
 * An unknown `variant` draws the default rather than nothing: the name comes
 * from a browser's localStorage, which may hold a choice from a newer build.
 * The default is written out here rather than imported from
 * utils/heroArtwork.js (DEFAULT_ARTWORK), because this file imports nothing;
 * DashboardHeroArtwork.test.jsx holds the two equal.
 */
export default function DashboardHeroArtwork({ variant = 'mycelium' }) {
  const draw = VARIANTS[variant] || VARIANTS.mycelium;
  return (
    <div data-testid="hero-artwork" data-variant={variant}
         style={{ position: 'absolute', inset: 0, borderRadius: 18, overflow: 'hidden', zIndex: 0 }}>
      {/* PITRIOT watermark */}
      <span style={{
        position: 'absolute',
        top: 18,
        insetInlineEnd: 24,
        color: 'rgba(255,255,255,0.13)',
        fontSize: 30,
        fontWeight: 900,
        letterSpacing: 5,
        userSelect: 'none',
        fontFamily: 'monospace',
      }}>PITRIOT</span>

      {draw()}
    </div>
  );
}
